import {
  AHText,
  Box,
  useScaleFactor,
  sectionListGetItemLayout,
  useLayoutContext,
} from "arkh";
import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Platform,
  SectionList,
  SectionListData,
  SectionListRenderItem,
  ViewToken,
} from "react-native";
import {
  TaskExecutionIssue,
  useTaskExecutionIssueHeight,
} from "./WidgetIssueOverviewIssueItem";
import { graphql, useFragment } from "react-relay";
import { WidgetOverviewSize } from "./__generated__/WidgetIssueOverviewData_widgetIssueOverview.graphql";
import { WidgetIssueOverviewList_issue$key } from "./__generated__/WidgetIssueOverviewList_issue.graphql";
import { FormattedMessage } from "react-intl";
import { Image } from "expo-image";
const listStyle = { flex: 1 } as const;

function resolveSectionTitle(section: {
  title: string | null | undefined;
  section: {
    assignedTeam: { name: string } | null | undefined;
  };
}) {
  if (section.title === "" || section.title === null) {
    return section.section.assignedTeam?.name ?? "";
  }
  return section.title;
}

export const conf = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

export type WidgetIssueOverviewRef = {
  scrollToItem: (itemId: string | null) => void;
};
export const WidgetIssueOverviewList = React.memo(
  React.forwardRef(_WidgetIssueOverviewList)
);
function _WidgetIssueOverviewList(
  props: {
    issues: WidgetIssueOverviewList_issue$key;
    isFocused?: boolean;
    interactive?: boolean;
    mode: "FOCUS" | "IDLE";
    onPressItem: (id: string) => void;
    setScroll: (itemId: string) => void;
    highlightedItemId?: string;
  },
  ref: ForwardedRef<WidgetIssueOverviewRef>
) {
  const issues = useFragment(
    graphql`
      fragment WidgetIssueOverviewList_issue on Issue @relay(plural: true) {
        id
        createdAt
        task {
          id
          title
          routine {
            id
            title
            ownerTeam {
              name
            }
          }
        }
        taskExecution {
          id
          sectionVersion {
            id
            ordinal
            title
            section {
              id
              assignedTeam {
                id
                name
              }
            }
          }
        }

        ...WidgetIssueOverviewIssueItem_issue
      }
    `,
    props.issues
  );

  type Issue = (typeof issues)[number];
  type SortedIssues = {
    id: string;
    routineTitle: string;
    teamName: string;
    sections: {
      id: string;
      title: string | null;
      ordinal: number;
      tasks: {
        id: string;
        title: string;
        issues: Issue[];
      }[];
    }[];
  };

  const routines = useMemo(
    () =>
      issues.reduce((routines, issue) => {
        const routineIndex = routines.findIndex(
          ({ id }) => id === issue.task.routine.id
        );
        if (routineIndex > -1) {
          return routines.map((routine, index) => {
            if (routineIndex === index) {
              const sectionIndex = routine.sections.findIndex(
                ({ id }) => id === issue.taskExecution.sectionVersion.section.id
              );
              if (sectionIndex > -1) {
                return {
                  ...routine,
                  sections: routine.sections.map((section, index) => {
                    if (index === sectionIndex) {
                      const taskIndex = section.tasks.findIndex(
                        (task) => task.id === issue.task.id
                      );
                      if (taskIndex > -1) {
                        return {
                          ...section,
                          tasks: section.tasks.map((task, index) => {
                            if (taskIndex === index) {
                              return {
                                ...task,
                                issues: [...task.issues, issue],
                              };
                            }
                            return task;
                          }),
                        };
                      }
                      return {
                        ...section,
                        tasks: [
                          ...section.tasks,
                          {
                            id: issue.task.id,
                            title: issue.task.title,
                            issues: [issue],
                          },
                        ],
                      };
                    }
                    return section;
                  }),
                };
              }
              return {
                ...routine,
                sections: [
                  ...routine.sections,
                  {
                    id: issue.taskExecution.sectionVersion.section.id,
                    title: resolveSectionTitle(
                      issue.taskExecution.sectionVersion
                    ),
                    ordinal: issue.taskExecution.sectionVersion.ordinal,
                    tasks: [
                      {
                        id: issue.task.id,
                        title: issue.task.title,
                        issues: [issue],
                      },
                    ],
                  },
                ],
              } as SortedIssues;
            }

            return routine;
          });
        }

        return [
          ...routines,
          {
            id: issue.task.routine.id,
            routineTitle: issue.task.routine.title,
            teamName: issue.task.routine.ownerTeam?.name ?? "",
            sections: [
              {
                id: issue.taskExecution.sectionVersion.section.id,
                title: resolveSectionTitle(issue.taskExecution.sectionVersion),
                ordinal: issue.taskExecution.sectionVersion.ordinal,
                tasks: [
                  {
                    id: issue.task.id,
                    title: issue.task.title,
                    issues: [issue],
                  },
                ],
              },
            ],
          },
        ] as SortedIssues[];
      }, [] as SortedIssues[]),
    [issues]
  );

  const sortedSections = useMemo(() => {
    return routines
      .map(
        (r) =>
          [
            r,
            r.sections
              .flatMap((section) =>
                section.tasks.flatMap((task) => task.issues)
              )
              .reduce<null | string>(
                (acc, issue) =>
                  acc
                    ? acc.localeCompare(issue.createdAt)
                      ? issue.createdAt
                      : acc
                    : issue.createdAt,
                null
              ),
          ] as const
      )
      .sort(([_ra, a], [_rb, b]) => (!a || !b ? 0 : b.localeCompare(a)))
      .map(([routine]) => ({
        key: routine.id,
        routineTitle: routine.routineTitle,
        teamName: routine.teamName,
        data: routine.sections
          .sort((a, b) => a.ordinal - b.ordinal)
          .flatMap((s) => s.tasks.flatMap((a) => a.issues)),
      }));
  }, [routines]);

  const renderItem: SectionListRenderItem<Issue> = useCallback(
    ({ item, index, section }) => (
      <TaskExecutionIssue
        key={item.id}
        isLast={index + 1 === section.data.length}
        isFocused={item.id === props.highlightedItemId}
        mode={props.mode}
        issue={item}
        onPress={props.onPressItem}
      />
    ),
    [props.highlightedItemId, props.mode, props.onPressItem]
  );
  const sf = useScaleFactor();
  const layout = useLayoutContext();

  const renderSectionHeader = useCallback(
    ({ section }: { section: SectionListData<Issue> }) => (
      <Box
        spaceTop="xsmall"
        spaceHorizontal="medium"
        style={{
          height: 30 * sf,
          justifyContent: "space-between",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <AHText numberOfLines={1} type="title-small">
          {section.routineTitle}{" "}
        </AHText>
        <AHText numberOfLines={1} type="detail">
          {section.teamName}
        </AHText>
      </Box>
    ),
    [sf]
  );

  const renderSectionFooter = useCallback(
    () => <Box spaceBottom="small" style={{}} />,
    []
  );

  const [viewableItems, setViewableItems] = useState<ViewToken[] | null>(null);
  const firstItem = useMemo(
    () =>
      viewableItems && props.isFocused && Platform.OS !== "web"
        ? viewableItems.find((i) => i.section?.key && i.item?.id)?.item?.id
        : null,
    [props.isFocused, viewableItems]
  );

  const { isFocused, setScroll } = props;
  useEffect(() => {
    if (isFocused && firstItem && Platform.OS !== "web") {
      setScroll(firstItem);
    }
  }, [firstItem, isFocused, setScroll]);

  const onViewableItemsChanged = useCallback(
    (e: { viewableItems: ViewToken[]; changed: ViewToken[] }) => {
      setViewableItems(e.viewableItems);
    },
    []
  );

  const sectionListRef = useRef<SectionList>(null);

  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId: string | null) => {
      const foundItem = itemId
        ? sortedSections
            .flatMap((s, sIndex) =>
              s.data.map((d, itemIndex) => [d.id, sIndex, itemIndex] as const)
            )
            .find(([id]) => id === itemId)
        : null;

      if (foundItem) {
        sectionListRef.current?.scrollToLocation({
          itemIndex: foundItem[2],
          sectionIndex: foundItem[1],
        });
      } else {
        sectionListRef.current?.scrollToLocation({
          itemIndex: 0,
          sectionIndex: 0,
        });
      }
    },
  }));

  const itemHeight = useTaskExecutionIssueHeight();
  const getItemLayout = sectionListGetItemLayout({
    getItemHeight: () => itemHeight,
    getSectionFooterHeight: () => layout.space.small * sf,
    getSectionHeaderHeight: () => 30 * sf,
    getSeparatorHeight: () => 0,
  });

  return (
    <SectionList
      ref={sectionListRef}
      getItemLayout={getItemLayout}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      ListEmptyComponent={NoIssuesComponent}
      sections={sortedSections}
      stickySectionHeadersEnabled={false}
      scrollEnabled={props.interactive !== false}
      renderSectionHeader={renderSectionHeader}
      renderSectionFooter={renderSectionFooter}
      renderItem={renderItem}
      viewabilityConfig={Platform.OS !== "web" ? conf : undefined}
      style={listStyle}
      onViewableItemsChanged={
        Platform.OS !== "web" ? onViewableItemsChanged : undefined
      }
    />
  );
}
export function NoIssuesComponent(props: { size: WidgetOverviewSize }) {
  return (
    <Box style={{ flex: 1, justifyContent: "center" }} color="surface">
      <Image
        source={require("./dance.png")}
        style={{
          width: "100%",
          height: "60%",
        }}
        contentFit="contain"
      />
      <Box style={{ alignItems: "center" }} spaceVertical="small">
        <AHText
          type={
            props.size === "TWO_X_ONE" || props.size === "TWO_X_TWO"
              ? "title"
              : "title-big"
          }
        >
          <FormattedMessage
            description="WidgetIssueOverview No Issues Title"
            defaultMessage="No Issues!"
          />
        </AHText>
      </Box>
    </Box>
  );
}
